import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { fmtMoney, getLang, useT } from '../lib/i18n';
import { fmtShortDay, fmtTime } from '../lib/time';
import { getToken } from '../lib/api';
import { haptic } from '../lib/telegram';

/**
 * «Заменить оплату»: the same lesson, paid the other way round.
 *
 * The coach's real problem is not "which mode is this booking in", it is
 * «он заплатил за сегодня наличными, а кредит пусть останется на ноябрь»
 * — one lesson's payment traded for another's. So the sheet is about two
 * lessons: this one, and the one that gives up a credit or takes it.
 *
 * Nothing moves until «Заменить оплату» is tapped, and the line above it
 * says in full what that tap will do.
 */

const BASE = import.meta.env.VITE_API_URL ?? '';

interface Options {
  booking: {
    bookingId: string; startsAt: string; status: string; mode: string; name: string;
    credits: number; owed: number; price: number | null; paid: boolean; cancelled: boolean;
  };
  freeCredits: number;
  /** Using a free credit now leaves these lessons ahead uncovered. */
  freeCostsLater: { lessons: number; cashDue: number } | null;
  donors: { bookingId: string; startsAt: string; status: string; credits: number; price: number | null }[];
  receivers: { bookingId: string; startsAt: string; status: string; owed: number }[];
}

async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    ...init,
    headers: { ...(init.body == null ? {} : { 'content-type': 'application/json' }),
      authorization: `Bearer ${getToken()}`, ...(init.headers ?? {}) },
  });
  if (!res.ok) throw new Error((await res.json().catch(() => ({})) as any).error ?? `http_${res.status}`);
  return res.status === 204 ? (undefined as T) : res.json();
}

/** Server refusals, in words. Anything unknown still says something. */
export function switchError(t: ReturnType<typeof useT>, code: string) {
  const known: Record<string, string> = {
    already_paid: 'co.sw.errPaid',
    cancelled: 'co.sw.errCancelled',
    no_free_credit: 'co.sw.errNoCredit',
    no_credit_on_it: 'co.sw.errNoCreditOnIt',
    donor_has_no_credit: 'co.sw.errDonorEmpty',
    donor_too_small: 'co.sw.errDonorSmall',
    donor_cancelled: 'co.sw.errCancelled',
    receiver_has_credit: 'co.sw.errReceiverHas',
    another_client: 'co.sw.errOtherClient',
    same_booking: 'co.sw.errSame',
    unknown_booking: 'co.sw.errGone',
    unknown_donor: 'co.sw.errGone',
    unknown_receiver: 'co.sw.errGone',
  };
  const key = known[code];
  return key ? t(key as 'co.sw.errPaid') : t('co.failed', { message: code });
}

const when = (iso: string) => `${fmtShortDay(new Date(iso))}, ${fmtTime(iso)}`;

export function PaySwitchSheet({ bookingId, onClose, onDone }: {
  bookingId: string; onClose: () => void; onDone: () => void;
}) {
  const t = useT();
  const [o, setO] = useState<Options | null>(null);
  const [pick, setPick] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    call<Options>(`/api/coach/bookings/${bookingId}/pay-mode`)
      .then((r) => {
        setO(r);
        // The latest lesson holding a credit, as the one to give it up:
        // the furthest away is the one the client will miss least.
        setPick(r.booking.mode === 'cash'
          ? (r.freeCredits > 0 ? 'free' : r.donors[0]?.bookingId ?? null)
          : 'free');
      })
      .catch((err) => setError(switchError(t, err.message)));
  }, [bookingId]);

  const toCredits = o?.booking.mode === 'cash';

  async function go() {
    if (!o) return;
    setBusy(true);
    setError(null);
    try {
      await call(`/api/coach/bookings/${bookingId}/pay-mode`, {
        method: 'POST',
        body: JSON.stringify(toCredits
          ? { to: 'credits', donor: pick === 'free' ? null : pick }
          : { to: 'cash', receiver: pick === 'free' ? null : pick }),
      });
      haptic.success();
      onDone();
      onClose();
    } catch (err: any) {
      // Never a dead tap: the sheet stays open and says what happened.
      haptic.error();
      setError(switchError(t, err.message));
      setBusy(false);
    }
  }

  /** What the tap will do, in one sentence, before it is tapped. */
  function preview() {
    if (!o) return null;
    const here = when(o.booking.startsAt);
    if (toCredits) {
      if (pick === 'free') return t('co.sw.prevFree', { lesson: here });
      const d = o.donors.find((x) => x.bookingId === pick);
      if (!d) return null;
      return t('co.sw.prevMove', {
        from: when(d.startsAt), to: here, price: fmtMoney(d.price ?? 0),
      });
    }
    if (pick === 'free') return t('co.sw.prevBack', { lesson: here, price: fmtMoney(o.booking.price ?? 0) });
    const r = o.receivers.find((x) => x.bookingId === pick);
    if (!r) return null;
    return t('co.sw.prevHand', { to: when(r.startsAt), lesson: here, price: fmtMoney(o.booking.price ?? 0) });
  }

  const choices = !o ? [] : toCredits
    ? [
        ...(o.freeCredits > 0
          ? [{ id: 'free', head: t('co.sw.useFree'), sub: t('co.sw.freeLeft', { n: fmtN(o.freeCredits), credits: t.word('book.credit', o.freeCredits) }) }]
          : []),
        ...o.donors.map((d) => ({
          id: d.bookingId,
          head: when(d.startsAt),
          sub: `${d.status === 'attended' ? t('co.sw.taught') : t('co.sw.booked')} · ${t('co.sw.becomesCash', { price: fmtMoney(d.price ?? 0) })}`,
        })),
      ]
    : [
        { id: 'free', head: t('co.sw.backToBalance'), sub: t('co.sw.backToBalanceSub') },
        ...o.receivers.map((r) => ({
          id: r.bookingId,
          head: when(r.startsAt),
          sub: t('co.sw.givesTo', { amount: fmtMoney(r.owed) }),
        })),
      ];

  const nothing = o && toCredits && o.freeCredits <= 0 && o.donors.length === 0;

  return createPortal(
    <div className="sheet" onClick={() => { if (!busy) onClose(); }}>
      <div className="sheet__panel" onClick={(e) => e.stopPropagation()}>
        <div className="sheet__title">{t('co.sw.title')}</div>
        {!o ? (
          error ? <div className="warnline">{error}</div> : <div className="skeleton" style={{ marginTop: 12 }} />
        ) : (
          <>
            <div className="card__when">
              {t('co.sw.now', {
                name: o.booking.name, lesson: when(o.booking.startsAt),
                how: o.booking.mode === 'cash' ? t('co.sw.inCash', { amount: fmtMoney(o.booking.owed) })
                  : t('co.sw.inCredits', { n: fmtN(o.booking.credits), credits: t.word('book.credit', o.booking.credits) }),
              })}
            </div>

            {o.booking.paid && <div className="warnline">{t('co.sw.errPaid')}</div>}
            {nothing && <div className="warnline">{t('co.sw.errNoCredit')}</div>}

            {!o.booking.paid && !nothing && (
              <div className="swlist">
                {choices.map((c) => (
                  <button key={c.id} className={`swopt${pick === c.id ? ' swopt--on' : ''}`}
                    onClick={() => { haptic.tap(); setPick(c.id); setError(null); }}>
                    <span className="swopt__mark" aria-hidden="true" />
                    <span className="swopt__text">
                      <b>{c.head}</b>
                      <small>{c.sub}</small>
                    </span>
                  </button>
                ))}
              </div>
            )}

            {!o.booking.paid && !nothing && pick && <div className="swprev">{preview()}</div>}
            {toCredits && pick === 'free' && o.freeCostsLater && (
              <div className="warnline">
                {t(o.freeCostsLater.cashDue > 0 ? 'co.sw.warnLater' : 'co.sw.warnLaterNoPrice', {
                  n: o.freeCostsLater.lessons,
                  lessons: t.word('co.sw.lessonsW', o.freeCostsLater.lessons),
                  amount: fmtMoney(o.freeCostsLater.cashDue),
                })}
              </div>
            )}
            {error && <div className="warnline">{error}</div>}

            <div className="sheet__actions">
              <button className="paybar__opt" onClick={onClose}>{t('co.offer.cancel')}</button>
              <button className="savebtn" disabled={busy || !pick || !!o.booking.paid || !!nothing} onClick={go}>
                {t('co.sw.confirm')}
              </button>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body,
  );
}

/** Credits as written: at most two decimals, a comma in Russian. */
function fmtN(n: number) {
  const v = String(Math.round(n * 100) / 100);
  return getLang() === 'ru' ? v.replace('.', ',') : v;
}
